import type { ServiceDetail } from "./types";

export const CONSULENZA_AZIENDALE: ServiceDetail = {
  slug: "consulenza-aziendale",
  category: "business",
  name: "Consulenza Aziendale",
  hero: {
    eyebrow: "Consulenza Aziendale",
    title: "Decisioni basate sui numeri, non sulle sensazioni.",
    subtitle:
      "Affianchiamo founder e direzioni di PMI italiane a leggere i propri dati, mettere ordine nei processi e costruire una roadmap strategica realistica. Sede a Roma, certificati ISO 9001:2015.",
    bullets: [
      "Audit completo di numeri, vendite e organizzazione in 2 settimane",
      "Business plan e modello finanziario a 12-36 mesi",
      "Dashboard KPI condivisa con board mensile operativo",
      "Affiancamento dell'imprenditore senza contratti pluriennali",
    ],
  },
  problem: [
    "La maggior parte delle PMI italiane cresce per inerzia: il fatturato sale, ma margini, ruoli e processi restano quelli del primo anno. Quando arriva un imprevisto — un cliente che salta, un competitor aggressivo, una marginalità che si erode — manca un cruscotto per capire cosa è successo davvero e dove intervenire.",
    "Il risultato è che le decisioni più importanti — assumere, investire, alzare i prezzi, aprire una linea di business, chiuderne un'altra — vengono prese tardi, sotto pressione, basate su sensazioni più che su dati. Il founder si ritrova a fare il pompiere invece che il regista, e la cassa diventa un'incognita di fine mese.",
    "A questo si aggiunge un problema strutturale: in azienda nessuno ha tempo di fermarsi e guardare i numeri con metodo. Il commercialista guarda al consuntivo fiscale, non alla strategia. Il management è operativo. Il founder è ovunque tranne che sul tavolo strategico. Serve un partner esterno che porti metodo, ritmo e una visione di lungo periodo.",
  ],
  approach: [
    "Non lavoriamo con slide e workshop motivazionali. Partiamo sempre dai numeri reali della tua azienda — bilancio, prima nota, CRM, ore vendute, scorte — e dalle persone chiave che li producono ogni giorno.",
    "Il nostro metodo è in quattro fasi: diagnosi rapida (2 settimane), definizione di una roadmap operativa con priorità e responsabili, implementazione concreta degli interventi a maggior impatto, monitoraggio mensile dei KPI con board del founder e del management.",
    "Restiamo accanto all'imprenditore per tutto il percorso: il nostro lavoro non finisce con la consegna del piano, ma con il vederlo eseguito. Ogni mese ci sediamo al tavolo, leggiamo i numeri insieme, decidiamo le mosse del mese successivo.",
    "Lavoriamo prevalentemente con micro e piccole imprese italiane di servizi, commercio e produzione, in cui il founder è ancora operativo. Conosciamo i vincoli reali di chi gestisce 5-50 persone: budget limitati, tempo zero, decisioni che pesano sul personale.",
  ],
  deliverables: [
    {
      title: "Audit aziendale 360°",
      detail:
        "Analisi completa di organizzazione, processi, cash flow, vendite e marketing in due settimane, con un report scritto di 20-30 pagine.",
    },
    {
      title: "Business plan operativo",
      detail:
        "Piano industriale a 12-36 mesi con scenari prudente / target / sfidante, ipotesi documentate e milestone trimestrali.",
    },
    {
      title: "Modello finanziario",
      detail:
        "Foglio di lavoro condiviso con conto economico, cash flow e fabbisogno finanziario mese per mese, aggiornato in board.",
    },
    {
      title: "Dashboard KPI",
      detail:
        "Cruscotto digitale con i 10-15 indicatori che contano davvero per la tua azienda, alimentato da gestionale e CRM esistenti.",
    },
    {
      title: "Board mensile",
      detail:
        "Riunione operativa di 90 minuti ogni mese con founder e management: lettura dei numeri, decisioni e azioni della settimana successiva.",
    },
  ],
  process: [
    {
      step: "01",
      title: "Settimana 1 — Discovery",
      text: "Call gratuita di 30 minuti, raccolta documenti (bilanci, P&L gestionale, CRM, organigramma) e interviste alle figure chiave dell'azienda.",
    },
    {
      step: "02",
      title: "Mese 1 — Audit & Roadmap",
      text: "Diagnosi 360°, identificazione delle 3 priorità a maggior impatto e consegna della roadmap operativa con responsabili e tempi.",
    },
    {
      step: "03",
      title: "Mese 3 — Esecuzione",
      text: "Implementazione degli interventi prioritari (riorganizzazione, pricing, controllo di gestione, processi di vendita) con il nostro affiancamento settimanale.",
    },
    {
      step: "04",
      title: "Ongoing — Board mensile",
      text: "Lettura mensile dei KPI, revisione della roadmap e affiancamento continuativo del founder. Contratti rinnovabili a 6 mesi, mai vincolanti.",
    },
  ],
  faq: [
    {
      q: "Per che tipo di aziende è pensato questo servizio?",
      a: "Lavoriamo soprattutto con micro e piccole imprese italiane: 5-50 persone, fatturato 0,5-10 M€, settori di servizi, commercio e produzione, in cui il founder è ancora operativo e ha bisogno di un partner esterno che porti metodo strategico e disciplina sui numeri.",
    },
    {
      q: "Quanto dura un percorso tipico di consulenza?",
      a: "L'audit iniziale dura 2 settimane. L'affiancamento successivo è in genere mensile, con contratti rinnovabili a 6 mesi. Non vincoliamo a contratti pluriennali: se dopo 6 mesi non vedi risultati misurabili, non hai vincoli.",
    },
    {
      q: "Quanto costa la consulenza aziendale di AB MARK?",
      a: "L'audit iniziale parte da 4.500 € + IVA. L'affiancamento mensile va da 1.500 a 4.500 € + IVA al mese in base al perimetro (audit, board mensile, implementazione interventi). Tutto è formalizzato in offerta scritta dopo la call gratuita di discovery.",
    },
    {
      q: "Operate solo a Roma o anche in altre città italiane?",
      a: "La sede operativa è a Roma in Via Alberto Cadlolo 92, ma lavoriamo con clienti in tutta Italia. La maggior parte delle attività si svolge da remoto, con incontri in presenza per audit iniziale, board strategici e momenti chiave del progetto.",
    },
    {
      q: "Cosa cambia con la vostra certificazione ISO 9001:2015?",
      a: "Significa che ogni progetto segue procedure tracciate e auditabili: dalla raccolta dei requisiti alla consegna dei deliverable, fino alla gestione delle revisioni. Per il cliente è la garanzia di un processo strutturato, non improvvisato — fondamentale anche per chi lavora con la Pubblica Amministrazione.",
    },
    {
      q: "Garantite esclusività territoriale o di settore?",
      a: "Non lavoriamo contemporaneamente con due aziende che competono direttamente sullo stesso mercato e segmento. È una regola che ci diamo per evitare conflitti di interesse e proteggere la riservatezza dei dati strategici dei nostri clienti.",
    },
  ],
  seo: {
    title: "Consulenza Aziendale Roma | Strategia per PMI | AB MARK",
    description:
      "Consulenza aziendale strategica per PMI italiane: audit dei numeri, business plan, dashboard KPI e board mensile. Sede a Roma, certificati ISO 9001:2015.",
  },
};
